import type { ResolvedTheme, ThemePreference } from './types';

export const THEME_STORAGE_KEY = 'theme';

const THEME_PREFERENCES: readonly ThemePreference[] = [
  'light',
  'dark',
  'system',
];

export function isThemePreference(value: string): value is ThemePreference {
  return (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function readThemePreference(): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system';
  }
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && isThemePreference(stored)) {
      return stored;
    }
  } catch {
    // ignore quota / private mode
  }
  return 'system';
}

/** @deprecated Use readThemePreference */
export const readStoredThemePreference = readThemePreference;

export function writeThemePreference(preference: ThemePreference): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    document.documentElement.setAttribute('data-theme-preference', preference);
  } catch {
    // ignore quota / private mode
  }
}

/** @deprecated Use writeThemePreference */
export const persistThemePreference = writeThemePreference;

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference;
}

export function applyResolvedTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.setAttribute('data-bs-theme', resolved);
  root.style.colorScheme = resolved;
}

export function applyThemeToDocument(
  preference: ThemePreference,
): ResolvedTheme {
  const resolved = resolveTheme(preference);
  applyResolvedTheme(resolved);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme-preference', preference);
  }
  return resolved;
}

export function subscribeToSystemTheme(
  onChange: (resolved: ResolvedTheme) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    onChange(getSystemTheme());
  };
  media.addEventListener('change', handler);
  return () => {
    media.removeEventListener('change', handler);
  };
}

/** Sync theme when another tab updates `localStorage` (storage event). */
export function subscribeToThemePreferenceChanges(
  onChange: (preference: ThemePreference) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handler = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) {
      return;
    }
    const stored = event.newValue;
    const preference =
      stored && isThemePreference(stored) ? stored : ('system' as const);
    onChange(preference);
  };

  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('storage', handler);
  };
}

/** Apply stored preference before React mounts (pairs with index.html inline script). */
export function initThemeFromStorage(): void {
  const preference = readThemePreference();
  applyResolvedTheme(resolveTheme(preference));
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme-preference', preference);
  }
}
